import {Code, ConnectError} from '@connectrpc/connect';
import {PlayerReadiness} from '../../generated/bay_session_shared_pb';
import type {ConfirmReadyToSwingResponse} from '../../generated/player_bay_session_pb';

class StaleConfirmReadyToSwingError extends Error {
  constructor() {
    super('ConfirmReadyToSwing was based on stale readiness state');
    this.name = 'StaleConfirmReadyToSwingError';
  }
}

class ReadyToSwingNotConfirmedError extends Error {
  constructor(readiness: PlayerReadiness | undefined) {
    const message = readiness === PlayerReadiness.CONFIRM ? 'Player approval is still required before play can continue.' : readiness === undefined || readiness === PlayerReadiness.UNSPECIFIED ? 'Session readiness could not be confirmed — please reload the app.' : 'Player is not ready to play yet.';
    super(message);
    this.name = 'ReadyToSwingNotConfirmedError';
  }
}

/**
 * Tags FailedPrecondition only when it came from ConfirmReadyToSwing. The
 * caller may invoke another RPC in the same promise chain, and that RPC's
 * FailedPrecondition must keep its normal error handling.
 */
export async function classifyConfirmReadyToSwingFailure<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();

  } catch (error) {
    const code = ConnectError.from(error).code;

    if (code === Code.FailedPrecondition) {
      throw new StaleConfirmReadyToSwingError();
    }

    throw error;
  }
}

/**
 * A successful RPC transport does not necessarily mean that every readiness
 * gate was cleared. In particular, GUARDIAN_REQUIRED remains authoritative
 * until the separate guardian flow completes.
 */
export async function confirmReadyToSwingAndRequirePlay(call: () => Promise<ConfirmReadyToSwingResponse>): Promise<ConfirmReadyToSwingResponse> {
  const response = await classifyConfirmReadyToSwingFailure(call);
  const readiness = response.player?.readiness;
  if (readiness !== PlayerReadiness.PLAY) {
    throw new ReadyToSwingNotConfirmedError(readiness);
  }
  return response;
}

/**
 * ConfirmReadyToSwing answers FailedPrecondition when the server refuses the
 * gesture against the state it holds. The prompt must stay actionable so the
 * next tap works off the latest streamed readiness.
 */
export function isStaleConfirmReadyToSwingError(error: unknown): boolean {
  return error instanceof StaleConfirmReadyToSwingError;
}

export function isReadyToSwingNotConfirmedError(error: unknown): boolean {
  return error instanceof ReadyToSwingNotConfirmedError;
}
